import { Injectable } from '@angular/core';
import { GeometryAssetMemoryService } from './geometry-asset-memory.service';
import { MorphAssetMemoryService } from './morph-asset-memory.service';
import { MemoryReport } from './models/memory-report.model';
import { MemoryGlobalStatsModel } from './models/memory-global-stats.model';
import {
  GeoStatsModel,
  MorphStatsModel,
  MemoryStatsSummaryModel,
} from './models/memory-stats.model';
import { HumanInterface } from '../engine/models/human.interface';

declare const Human: HumanInterface;

@Injectable({
  providedIn: 'root',
})
export class MemoryService {
  constructor(
    private geometryAssetMemoryService: GeometryAssetMemoryService,
    private morphAssetMemoryService: MorphAssetMemoryService,
  ) {}

  /* Stats for memory usage
   * Actual or Live stats are the statistics for the assets as loaded.
   * Scenario stats are the staistics that change in different scenarios:
   *      - with or without geometry compression
   *      - with or without texture compression on desktop, ios, android
   */

  engineSupportsGeometryCompression(): boolean {
    // geometry and morph compression
    return Human.assets.geometries.hasOwnProperty('getCompressionStats');
  }

  engineSupportsTextureCompression(): boolean {
    // texture compression
    return Human.assets.materials.hasOwnProperty('getTextureCompressionStats');
  }

  getFirstChapterDownloadSize(stats: MemoryGlobalStatsModel): number {
    if (Human.stats && Human.stats.trackingDownloadSize) {
      return (
        Human.stats.geometryDownloadTotal +
        Human.stats.textureDownloadTotal +
        Human.stats.jsonDownloadTotal
      );
    }

    return 0;
  }

  updateGlobalStats(
    geoStats: MemoryStatsSummaryModel<GeoStatsModel>,
    morphStats: MemoryStatsSummaryModel<MorphStatsModel>,
  ): MemoryGlobalStatsModel {
    const stats = new MemoryGlobalStatsModel();

    stats.memory = this.getTotalMem(geoStats, morphStats);
    stats.textureMemory = this.getTotalTexMem();
    stats.geometryMemory = this.getTotalGeoMem(geoStats);
    stats.morphMemory = this.getTotalMorphMem(morphStats);

    // Download size
    if (Human.stats && Human.stats.trackingDownloadSize) {
      stats.downloadSize =
        Human.stats.geometryDownloadTotal +
        Human.stats.textureDownloadTotal +
        Human.stats.jsonDownloadTotal;

      stats.geometryDownloadTotal = Human.stats.geometryDownloadTotal;
      stats.textureDownloadTotal = Human.stats.textureDownloadTotal;
      stats.jsonDownloadTotal = Human.stats.jsonDownloadTotal;
    }

    stats.uncompressedGeos = Object.values(Human.assets.geometries.geometries).reduce(
      (result: number, asset: any) => {
        if (!asset.compressed) {
          result++;
        }
        return result;
      },
      0,
    ) as number;

    let morphcore;
    stats.uncompressedMorphs = Object.values(Human.assets.morphs.morphs).reduce(
      (result: number, asset: any) => {
        morphcore = asset.morphGeometry._core;
        if (!morphcore.compressedNormals || !morphcore.compressedPositions) {
          result++;
        }
        return result;
      },
      0,
    ) as number;

    stats.uncompressedTextures = Object.entries(Human.assets.materials.materials).reduce(
      (result: number, [key, asset]: any) => {
        asset.textures.forEach((texture) => {
          if (!texture._core.compressed) {
            result++;
          }
        });
        return result;
      },
      0,
    ) as number;

    return stats;
  }

  updateGeoStats(
    stats?: MemoryStatsSummaryModel<GeoStatsModel>,
  ): MemoryStatsSummaryModel<GeoStatsModel> {
    if (stats == null) {
      stats = new MemoryStatsSummaryModel<GeoStatsModel>();
    }

    Object.keys(Human.assets.geometries.geometries).forEach((geometryId) => {
      if (!stats.hasOwnProperty(geometryId)) {
        stats[geometryId] = this.geometryAssetMemoryService.getStat(geometryId);
      }
    });

    return stats;
  }

  updateMorphStats(
    stats?: MemoryStatsSummaryModel<MorphStatsModel>,
  ): MemoryStatsSummaryModel<MorphStatsModel> {
    if (stats == null) {
      stats = new MemoryStatsSummaryModel<MorphStatsModel>();
    }

    Object.keys(Human.assets.morphs.morphs).forEach((morphId) => {
      if (!stats.hasOwnProperty(morphId)) {
        stats[morphId] = this.morphAssetMemoryService.getStat(morphId);
      }
    });

    return stats;
  }

  // Get Geometry Memory Usage for all geometry assets
  getTotalGeoMem(stats: MemoryStatsSummaryModel<GeoStatsModel>): number {
    return Object.values(stats).reduce((result: number, stat: any) => {
      result += stat.memory;
      return result;
    }, 0) as number;
  }

  // Get Morph Memory Usage for all morphing assets
  getTotalMorphMem(stats: MemoryStatsSummaryModel<MorphStatsModel>): number {
    return Object.values(stats).reduce((result: number, stat: any) => {
      result += stat.memory;
      return result;
    }, 0) as number;
  }

  // Get Texture Memory Usage for all downloaded textures
  getTotalTexMem(): number {
    return Object.values(Human.renderer.getScene().stats.textures).reduce(
      (result: number, stat: any) => {
        result += stat.memory;
        return result;
      },
      0,
    ) as number;
  }

  // Get Total Memory
  getTotalMem(
    geoStats: MemoryStatsSummaryModel<GeoStatsModel>,
    morphStats: MemoryStatsSummaryModel<MorphStatsModel>,
  ): number {
    return (
      this.getTotalGeoMem(geoStats) + this.getTotalMorphMem(morphStats) + this.getTotalTexMem()
    );
  }

  buildSceneGeometryReport(stats: MemoryStatsSummaryModel<GeoStatsModel>): MemoryReport {
    const shapeCount = Object.keys(stats).length;
    let shapeVerts = 0;
    let shapeTris = 0;
    let memory = 0.0;

    Object.values(stats).forEach((stat: any) => {
      shapeTris += stat.tris;
      shapeVerts += stat.verts;
      memory += stat.memory;
    });

    const countReport = new MemoryReport('Shape Count (uninstanced)', shapeCount, null);
    const trisReport = new MemoryReport('Triangle Count (uninstanced)', shapeTris, null);
    const vertsReport = new MemoryReport('Vertex Count (uninstanced)', shapeVerts, null);
    return new MemoryReport('Geometry Memory', memory, 'bytes', [
      countReport,
      trisReport,
      vertsReport,
    ]);
  }

  // All Morphs Loaded in Scene
  buildSceneMorphReport(stats: MemoryStatsSummaryModel<MorphStatsModel>): MemoryReport {
    const morphCount = Object.keys(stats).length;
    let morphVerts = 0;
    let memory = 0;

    Object.values(stats).forEach((stat: any) => {
      morphVerts += stat.verts;
      memory += stat.memory;
    });

    const countReport = new MemoryReport('Morph Count (uninstanced)', morphCount, null);
    const vertReport = new MemoryReport('Vertex Count (uninstanced)', morphVerts, null);
    return new MemoryReport('Morph Memory', memory, 'bytes', [countReport, vertReport]);
  }

  // All Textures Loaded in Scene
  buildSceneTextureReport(): MemoryReport {
    const textureStats = Human.renderer.getScene().stats.textures;
    let memory = 0;

    const textures = {
      baseColor: 'diffuse',
      alpha: 'alpha',
      emit: 'emit',
      normals: 'normals',
      specular: 'specular',
      texture: 'texture',
    };
    const textureReports = {};

    Object.entries(textures).forEach(([key, label]) => {
      const stats = this.generateTextureSummary(textureStats[key]);
      const reports = [...stats.reports];

      // reports.push(new MemoryReport('total', stats.subtotal, null));
      textureReports[label] = new MemoryReport(label, stats.submemory, 'bytes', reports);

      memory += stats.submemory;
    });

    return new MemoryReport('Texture Memory', memory, 'bytes', Object.values(textureReports));
  }

  // All Morphs Loaded in Scene
  buildSceneDownloadReport(stats: MemoryGlobalStatsModel): MemoryReport {
    if (!stats) {
      return null;
    }

    const total =
      stats.geometryDownloadTotal + stats.textureDownloadTotal + stats.jsonDownloadTotal;
    const geometryReport = new MemoryReport('Geometry', stats.geometryDownloadTotal, 'bytes');
    const textureReport = new MemoryReport('Textures', stats.textureDownloadTotal, 'bytes');
    const jsonReport = new MemoryReport('JSON', stats.jsonDownloadTotal, 'bytes');

    return new MemoryReport('Download Size', total, 'bytes', [
      geometryReport,
      textureReport,
      jsonReport,
    ]);
  }

  toMB(bytes: number) {
    return (bytes / 1048576).toFixed(2);
  }

  private generateTextureSummary(texture = {}): {
    reports: MemoryReport[];
    submemory: number;
    subtotal: number;
  } {
    const reports: MemoryReport[] = [];
    let subtotal = 0;
    let submemory = 0.0;
    Object.entries(texture).forEach((entry) => {
      const key = <string>entry[0];
      const value = <number>entry[1];

      if (key === 'memory') {
        submemory = value;
      } else {
        reports.push(new MemoryReport(key, value, null));
        subtotal += value;
      }
    });

    return {
      reports: reports,
      submemory: submemory,
      subtotal: subtotal,
    };
  }
}
